import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import EditOrderModal from "../components/editOrderModal";
import SpecialOrderModal from "../components/specialOrderModal";
import { toast } from "react-toastify";
import { confirmAlert } from "react-confirm-alert";
import "react-confirm-alert/src/react-confirm-alert.css";
import { mapToArabic } from "../utils/mapToArabic";
import axios from "axios";
import fetcher from "../utils/fetcher";
import useSWR from "swr";
import { mutate } from "swr";
import Pagination from "../components/Pagination";
import ProductImage from "../components/ProductImage";
import mediaUrl from "../utils/mediaUrl";
import {
  FiEdit,
  FiTrash2,
  FiPackage,
  FiChevronDown,
  FiMapPin,
  FiTruck,
  FiFileText,
  FiCalendar,
  FiImage,
  FiPlus,
} from "react-icons/fi";
import { BsBag, BsWhatsapp } from "react-icons/bs";
import { HiCreditCard, HiOutlineSparkles } from "react-icons/hi";
import { MdAttachMoney } from "react-icons/md";
import { FaRuler, FaRegLightbulb } from "react-icons/fa";
import styles from "../styles/orders.module.css";

const Orders = () => {
  const navigate = useNavigate();
  const user = useSelector((state) => state.auth.user);
  const [tab, setTab] = useState("regular");
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const [openModal, setOpenModal] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [isVisible, setIsVisible] = useState(true);

  const [currentPage, setCurrentPage] = useState(1);
  const [ordersPerPage] = useState(10);

  useEffect(() => {
    const handleScroll = () => {
      // Check if near bottom (hide WhatsApp button)
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop =
        window.pageYOffset || document.documentElement.scrollTop;

      // Hide when within 300px of bottom (adjust as needed)
      const isNearBottom = scrollTop + windowHeight >= documentHeight - 200;
      setIsVisible(!isNearBottom);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    // Initial check
    handleScroll();

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const ordersKey = `${process.env.REACT_APP_BACKEND_URL}/api/orders?page=${currentPage}&limit=${ordersPerPage}`;
  const specialKey = `${process.env.REACT_APP_BACKEND_URL}/api/orders/fetchSpecial`;

  const swrOptions = {
    revalidateOnFocus: false,
    revalidateIfStale: true,
    shouldRetryOnError: (err) => err.status !== 304,
    errorRetryCount: 2,
    dedupingInterval: 10000,
  };

  const { data, error, isValidating } = useSWR(ordersKey, fetcher, swrOptions);

  const {
    data: specialData,
    error: specialError,
    isValidating: specialValidating,
  } = useSWR(tab === "special" ? specialKey : null, fetcher, swrOptions);

  const isLoadingInitial = !data || isValidating;
  const isLoadingSpecial = !specialData || specialValidating;

  const orders = data?.orders || [];
  const totalOrders = data?.totalCount ? Number(data?.totalCount) : 0;
  const totalPages = Math.ceil(totalOrders / ordersPerPage);

  const specialOrders = specialData?.orders || [];

  const toggleExpand = (id) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  const handleEdit = (order) => {
    setSelectedOrder(order);
    setOpenModal(true);
  };

  const closeModal = () => {
    setOpenModal(false);
    setSelectedOrder(null);
  };

  const handleCancel = (order) => {
    confirmAlert({
      message: `إلغاء طلب رقم ${order.id} ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setCancelling(true);
            try {
              await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/cancel`,
                { withCredentials: true },
              );
              toast.success(
                <span className="toast">تم إلغاء الطلب بنجاح</span>,
              );

              mutate(ordersKey);
            } catch (error) {
              toast.error(
                <span className="toast">
                  لم نتمكن من إلغاء الطلب. حاول مرة اخرى
                </span>,
              );
            } finally {
              setCancelling(false);
            }
          },
        },
        {
          label: "لا",
        },
      ],
    });
  };

  const handleCancelSpecial = (order) => {
    const decorCloudinaryIds =
      order.decorImgs?.map((img) => img.cloudinary_id) || [];
    const exampleCloudinaryId = order.exampleImg?.cloudinary_id || null;

    confirmAlert({
      message: `إلغاء طلب رقم ${order.id} ؟`,
      buttons: [
        {
          label: "نعم",
          onClick: async () => {
            setCancelling(true);
            try {
              await axios.delete(
                `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/cancelSpecial`,
                {
                  data: { decorCloudinaryIds, exampleCloudinaryId },
                  withCredentials: true,
                },
              );
              toast.success(
                <span className="toast">تم إلغاء الطلب بنجاح</span>,
              );

              mutate(specialKey);
            } catch (error) {
              toast.error(
                <span className="toast">
                  لم نتمكن من إلغاء الطلب. حاول مرة اخرى
                </span>,
              );
            } finally {
              setCancelling(false);
            }
          },
        },
        {
          label: "لا",
        },
      ],
    });
  };

  const statusClass = (status) =>
    ({
      new: styles.stNew,
      preparing: styles.stPreparing,
      "out for delivery": styles.stOut,
      cancelled: styles.stCancelled,
    })[status] || styles.stDelivered;

  const skeleton = (
    <div className={styles.list}>
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className={styles.skelCard}>
          <span className={`${styles.skelChip} skeleton`}></span>
          <span className={`${styles.skelLine} skeleton`}></span>
          <span className={`${styles.skelChip} skeleton`}></span>
          <span className={`${styles.skelPrice} skeleton`}></span>
        </div>
      ))}
    </div>
  );

  const errorBox = (
    <div className={styles.empty}>
      <span className={styles.emptyIcon}><FiPackage /></span>
      <h2 className={styles.emptyTitle}>حدث خطأ اثناء تحميل الطلبات</h2>
      <p className={styles.emptySub}>تأكد من اتصالك بالانترنت وحاول مرة اخرى</p>
    </div>
  );

  const regularContent =
    error && error.status !== 304 ? (
      errorBox
    ) : isLoadingInitial ? (
      skeleton
    ) : orders.length === 0 ? (
      <div className={styles.empty}>
        <span className={styles.emptyIcon}><FiPackage /></span>
        <h2 className={styles.emptyTitle}>لم تقم بعمل طلبات بعد</h2>
        <p className={styles.emptySub}>
          اكتشف لوحاتنا المميزة وقم بعمل اول طلب لك الآن
        </p>
        <button
          className={styles.emptyCta}
          onClick={() => navigate(`/products`)}
        >
          <BsBag /> ابدأ التسوق
        </button>
      </div>
    ) : (
      <>
        <div className={styles.list}>
          {orders.map((order, i) => {
            let total = 0;

            for (const item of order.items) {
              const itemTotal = (item.price || 0) * (item.quantity || 1);
              total += itemTotal;
            }

            const discount = ((order.discount * total) / 100).toFixed(2);

            const net = (
              total -
              (discount || 0) +
              Number(order.fees || 0)
            ).toFixed(2);

            const isOpen = !!expanded[order.id];

            return (
              <article
                key={order.id}
                className={`${styles.card} ${isOpen ? styles.cardOpen : ""}`}
                style={{ "--i": i }}
              >
                <button
                  className={styles.cardHead}
                  onClick={() => toggleExpand(order.id)}
                  aria-expanded={isOpen}
                >
                  <span className={styles.orderId}>
                    <FiPackage />
                    طلب #{order.id}
                  </span>

                  <span
                    className={`${styles.status} ${statusClass(order.status)}`}
                  >
                    <i></i>
                    {mapToArabic(order.status).text}
                  </span>

                  {order.payment === "cash on delivery" ? (
                    <span className={styles.payChip}>
                      <MdAttachMoney /> نقداً
                    </span>
                  ) : order.payment === "credit card" ? (
                    <span className={styles.payChip}>
                      <HiCreditCard /> بطاقة
                    </span>
                  ) : null}

                  <span className={styles.date}>
                    <FiCalendar />
                    {new Date(order.created_at).toLocaleDateString("ar", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </span>

                  <span className={styles.net}>
                    <small>الصافي</small>
                    {net} ₪
                  </span>

                  <span className={styles.chevron}>
                    <FiChevronDown />
                  </span>
                </button>

                <div className={styles.details}>
                  <div className={styles.detailsInner}>
                    <div className={styles.infoGrid}>
                      <div className={styles.infoBox}>
                        <FiMapPin />
                        <div>
                          <p className={styles.infoLabel}>العنوان</p>
                          <p className={styles.infoValue}>
                            {order.street}
                            {order.district && <>, {order.district}</>}
                          </p>
                        </div>
                      </div>

                      <div className={styles.infoBox}>
                        <FiTruck />
                        <div>
                          <p className={styles.infoLabel}>منطقة التوصيل</p>
                          <p className={styles.infoValue}>{order.area}</p>
                        </div>
                      </div>

                      {order.note && (
                        <div className={styles.infoBox}>
                          <FiFileText />
                          <div>
                            <p className={styles.infoLabel}>ملاحظة</p>
                            <p className={styles.infoValue}>{order.note}</p>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className={styles.items}>
                      {order.items.map((item) => (
                        <div
                          key={item.id}
                          className={styles.item}
                          onClick={() =>
                            navigate(`/product/${item.product_id}`)
                          }
                        >
                          <div className={styles.thumb}>
                            <ProductImage
                              url={item.image_url}
                              alt={item.name}
                              loading="lazy"
                            />
                          </div>

                          <div className={styles.itemInfo}>
                            <p className={styles.itemName}>{item.name}</p>
                            <div className={styles.itemChips}>
                              <span className={styles.chip}>
                                {item.width} × {item.height} سم
                              </span>
                              <span
                                className={`${styles.chip} ${styles.chipGold}`}
                              >
                                {mapToArabic(item.material).text}
                              </span>
                            </div>
                          </div>

                          <div className={styles.itemEnd}>
                            <span className={styles.itemQty}>
                              {item.quantity} × {item.price} ₪
                            </span>
                            <span className={styles.itemTotal}>
                              {(item.price * item.quantity).toFixed(2)} ₪
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className={styles.totals}>
                      <div className={styles.totalRow}>
                        <span>الإجمالي</span>
                        <span>{total.toFixed(2)} ₪</span>
                      </div>
                      <div className={styles.totalRow}>
                        <span>رسوم التوصيل</span>
                        <span>{order.fees || 0} ₪</span>
                      </div>
                      {order.promocode && (
                        <div
                          className={`${styles.totalRow} ${styles.discountRow}`}
                        >
                          <span>الخصم</span>
                          <span>- {discount || 0} ₪</span>
                        </div>
                      )}
                      <div className={styles.netRow}>
                        <span>الصافي</span>
                        <strong>{net} ₪</strong>
                      </div>
                    </div>

                    {order.status === "new" && (
                      <div className={styles.actions}>
                        {order.payment === "cash on delivery" && (
                          <button
                            onClick={() => handleEdit(order)}
                            className={styles.editBtn}
                            disabled={cancelling}
                          >
                            <FiEdit /> تعديل الطلب
                          </button>
                        )}

                        <button
                          onClick={() =>
                            cancelling ? null : handleCancel(order)
                          }
                          className={styles.cancelBtn}
                          disabled={cancelling}
                        >
                          <FiTrash2 /> إلغاء الطلب
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            );
          })}
        </div>

        {totalPages > 1 && (
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
            disabled={isLoadingInitial}
          />
        )}
      </>
    );

  const specialContent =
    specialError && specialError.status !== 304 ? (
      errorBox
    ) : isLoadingSpecial ? (
      skeleton
    ) : specialOrders.length === 0 ? (
      <div className={styles.empty}>
        <span className={styles.emptyIcon}><HiOutlineSparkles /></span>
        <h2 className={styles.emptyTitle}>لم تقم بعمل طلبات خاصة بعد</h2>
        <p className={styles.emptySub}>
          ارسل لنا صورة حائطك وسنصمم لك لوحة فريدة تناسب مساحتك وذوقك
        </p>
        <button
          className={styles.emptyCta}
          onClick={() => setCreateOpen(true)}
        >
          <HiOutlineSparkles /> صمم لوحتك الآن
        </button>
      </div>
    ) : (
      <div className={styles.list}>
        {specialOrders.map((order, i) => {
          const isOpen = !!expanded[`s${order.id}`];

          return (
            <article
              key={order.id}
              className={`${styles.card} ${isOpen ? styles.cardOpen : ""}`}
              style={{ "--i": i }}
            >
              <button
                className={styles.cardHead}
                onClick={() => toggleExpand(`s${order.id}`)}
                aria-expanded={isOpen}
              >
                <span className={styles.orderId}>
                  <HiOutlineSparkles />
                  طلب خاص #{order.id}
                </span>

                <span
                  className={`${styles.status} ${statusClass(order.status)}`}
                >
                  <i></i>
                  {mapToArabic(order.status).text}
                </span>

                <span className={styles.payChip}>
                  <FaRuler /> عرض الحائط {order.wallWidth} م
                </span>

                <span className={styles.date}>
                  <FiCalendar />
                  {new Date(order.created_at).toLocaleDateString("ar", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>

                <span className={styles.chevron}>
                  <FiChevronDown />
                </span>
              </button>

              <div className={styles.details}>
                <div className={styles.detailsInner}>
                  <div className={styles.infoGrid}>
                    <div className={styles.infoBox}>
                      <FiMapPin />
                      <div>
                        <p className={styles.infoLabel}>العنوان</p>
                        <p className={styles.infoValue}>
                          {user?.street}
                          {user?.district && <>, {user.district}</>}
                        </p>
                      </div>
                    </div>

                    <div className={styles.infoBox}>
                      <FaRuler />
                      <div>
                        <p className={styles.infoLabel}>عرض الحائط</p>
                        <p className={styles.infoValue}>{order.wallWidth} متر</p>
                      </div>
                    </div>

                    {order.order_note && (
                      <div className={styles.infoBox}>
                        <FaRegLightbulb />
                        <div>
                          <p className={styles.infoLabel}>ملاحظاتك</p>
                          <p className={styles.infoValue}>{order.order_note}</p>
                        </div>
                      </div>
                    )}
                  </div>

                  {order.decorImgs?.length > 0 && (
                    <div className={styles.gallery}>
                      <p className={styles.galleryTitle}>
                        <FiImage /> صور الديكور
                      </p>
                      <div className={styles.galleryGrid}>
                        {order.decorImgs.map((img) => (
                          <a
                            key={img.image_url}
                            className={styles.galleryThumb}
                            href={mediaUrl(img.image_url)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <ProductImage
                              url={img.image_url}
                              alt="صورة الديكور"
                              loading="lazy"
                            />
                          </a>
                        ))}
                      </div>
                    </div>
                  )}

                  {order.exampleImg && (
                    <div className={styles.gallery}>
                      <p className={styles.galleryTitle}>
                        <FaRegLightbulb /> صورة تقريبية للفكرة
                      </p>
                      <div className={styles.galleryGrid}>
                        <a
                          className={styles.galleryThumb}
                          href={mediaUrl(order.exampleImg.image_url)}
                          target="_blank"
                          rel="noreferrer"
                        >
                          <ProductImage
                            url={order.exampleImg.image_url}
                            alt="صورة الفكرة"
                            loading="lazy"
                          />
                        </a>
                      </div>
                    </div>
                  )}

                  <div className={styles.actions}>
                    <button
                      onClick={() =>
                        cancelling ? null : handleCancelSpecial(order)
                      }
                      className={styles.cancelBtn}
                      disabled={cancelling}
                    >
                      <FiTrash2 /> إلغاء الطلب
                    </button>
                  </div>
                </div>
              </div>
            </article>
          );
        })}
      </div>
    );

  return (
    <div className={styles.page}>
      <a href="https://wa.me/972543255575" target="_blank" rel="noreferrer">
        <BsWhatsapp
          className={`whatsapp ${isVisible ? "showWhats" : "hideWhats"}`}
        />
      </a>

      <nav className={styles.breadcrumb}>
        <Link to="/">الرئيسية</Link>
        <FiChevronDown style={{ transform: "rotate(90deg)" }} />
        <span>طلباتك</span>
      </nav>

      <header className={styles.header}>
        <h1 className={styles.title}>
          <span className={styles.titleIcon}><FiPackage /></span>
          طلبـاتـك
        </h1>
        {tab === "regular" && totalOrders > 0 && (
          <span className={styles.countChip}>{totalOrders} طلب</span>
        )}
        {tab === "special" && specialOrders.length > 0 && (
          <span className={styles.countChip}>{specialOrders.length} طلب</span>
        )}
        <button
          className={styles.specialBtn}
          onClick={() => setCreateOpen(true)}
        >
          <FiPlus /> صمم لوحتك
        </button>
      </header>

      <div className={styles.tabs}>
        <button
          className={`${styles.tabBtn} ${tab === "regular" ? styles.tabActive : ""}`}
          onClick={() => setTab("regular")}
        >
          <FiPackage /> الطلبات
        </button>
        <button
          className={`${styles.tabBtn} ${tab === "special" ? styles.tabActive : ""}`}
          onClick={() => setTab("special")}
        >
          <HiOutlineSparkles /> الطلبات الخاصة
        </button>
      </div>

      {tab === "regular" ? regularContent : specialContent}

      <EditOrderModal
        order={selectedOrder}
        isOpen={openModal}
        onClose={closeModal}
        onSuccess={() => mutate(ordersKey)}
      />

      <SpecialOrderModal
        isOpen={createOpen}
        onClose={() => {
          setCreateOpen(false);
          mutate(specialKey);
        }}
      />
    </div>
  );
};

export default Orders;
